import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { TrendingUp, TrendingDown, Info } from "lucide-react";

export default function PortfolioHoldings({ portfolioId }) {
    const [holdings, setHoldings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (portfolioId) {
            fetchHoldings();
        }
    }, [portfolioId]);

    const fetchHoldings = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/api/portfolios/holdings/?portfolio=${portfolioId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHoldings(response.data);
        } catch (error) {
            console.error("Error fetching holdings:", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500 text-sm">Loading holdings...</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="bg-gray-50/50 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                        <th className="px-6 py-4">Symbol</th>
                        <th className="px-6 py-4">Shares</th>
                        <th className="px-6 py-4 text-right">Avg Cost</th>
                        <th className="px-6 py-4 text-right">Market Price</th>
                        <th className="px-6 py-4 text-right">Total Value</th>
                        <th className="px-6 py-4 text-right">Unrealized P&L</th>
                        <th className="px-6 py-4 text-right">Return (%)</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {holdings.filter(h => h.quantity > 0).map((h) => (
                        <tr key={h.id} className="hover:bg-gray-50/80 transition-colors group">
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{h.symbol.symbol}</span>
                                    <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-500 font-medium">
                                        {h.symbol.exchange_name}
                                    </span>
                                </div>
                                <div className="text-[11px] text-gray-400 mt-0.5 truncate max-w-[150px]">
                                    {h.symbol.name}
                                </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                                {parseFloat(h.quantity).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 text-right">
                                ${parseFloat(h.average_cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">
                                ${parseFloat(h.market_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 text-right font-bold">
                                ${parseFloat(h.total_value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className={`px-6 py-4 text-sm text-right font-bold ${parseFloat(h.unrealized_pnl) >= 0 ? "text-green-600" : "text-red-600"
                                }`}>
                                {parseFloat(h.unrealized_pnl) >= 0 ? "+" : ""}${Math.abs(h.unrealized_pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${parseFloat(h.unrealized_pnl_pct) >= 0
                                        ? "bg-green-50 text-green-700"
                                        : "bg-red-50 text-red-700"
                                    }`}>
                                    {parseFloat(h.unrealized_pnl_pct) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                    {Math.abs(h.unrealized_pnl_pct).toFixed(2)}%
                                </div>
                            </td>
                        </tr>
                    ))}
                    {holdings.length === 0 && (
                        <tr>
                            <td colSpan="7" className="px-6 py-12 text-center text-gray-400 italic text-sm">
                                No active holdings found in this portfolio.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <div className="px-6 py-4 bg-gray-50/30 border-t border-gray-50 flex items-center gap-2 text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
                <Info size={12} />
                Prices are synced from the screening engine and may be delayed.
            </div>
        </div>
    );
}
